import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  KpiCard,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Building2, Clock, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { daysUntil, tenantStatusTone } from '@/features/platform/labels';
import { platformOverviewQueryOptions } from '@/features/platform/queries';
import { MySupportAccessCard } from '@/features/support/SupportAccessCard';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateShort, formatRelative } from '@/lib/format';

export const Route = createFileRoute('/_app/platform/')({ component: PlatformOverviewPage });

/** Vue d'ensemble de la plateforme (ADR-021) : tenants, plans, modules, essais à échéance. */
function PlatformOverviewPage() {
  const { t, i18n } = useTranslation(['platform', 'common', 'errors', 'settings']);
  const session = useSession();
  const canRead = session.can('platform.read_health');
  const query = useQuery(platformOverviewQueryOptions(canRead));

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('platform:overview.title')}
        subtitle={t('platform:overview.subtitle')}
        breadcrumb={[{ label: t('platform:title') }, { label: t('platform:overview.title') }]}
        actions={
          <Button variant="primary" asChild>
            <Link to="/platform/tenants" search={{ new: true }}>
              <Building2 className="ico" aria-hidden="true" />
              {t('platform:tenants.new')}
            </Link>
          </Button>
        }
      />

      {!session.user.mfaEnabled ? (
        <Notice
          tone="warning"
          icon={<ShieldAlert className="ico" aria-hidden="true" />}
          title={t('platform:mfa.title')}
        >
          {t('platform:mfa.body')} <Link to="/me">{t('platform:mfa.link')}</Link>
        </Notice>
      ) : null}

      {query.isPending ? (
        <div className="stack" aria-busy="true">
          <Skeleton height={120} />
          <Skeleton height={260} />
        </div>
      ) : query.isError || !query.data ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : (
        <div className="stack">
          <MySupportAccessCard />
          <div className="grid grid-4">
            <KpiCard
              label={t('platform:overview.kpi.total')}
              value={query.data.tenants.total}
              tone="violet"
            />
            <KpiCard
              label={t('platform:status.TRIAL')}
              value={query.data.tenants.byStatus.TRIAL ?? 0}
              tone="amber"
            />
            <KpiCard
              label={t('platform:status.ACTIVE')}
              value={query.data.tenants.byStatus.ACTIVE ?? 0}
              tone="green"
            />
            <KpiCard
              label={t('platform:status.SUSPENDED')}
              value={query.data.tenants.byStatus.SUSPENDED ?? 0}
              tone={(query.data.tenants.byStatus.SUSPENDED ?? 0) > 0 ? 'red' : 'blue'}
            />
          </div>

          <div className="grid-2">
            <Card>
              <CardHeader title={t('platform:overview.plans')} />
              <CardBody density="flush">
                {query.data.subscriptions.length === 0 ? (
                  <EmptyState title={t('platform:overview.noSubscription')} />
                ) : (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('platform:overview.columns.plan')}</Th>
                          <Th align="right">{t('platform:overview.columns.tenants')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {query.data.subscriptions.map((row) => (
                          <Tr key={row.planCode}>
                            <Td>{row.planName}</Td>
                            <Td numeric align="right">
                              {row.count}
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader title={t('platform:overview.modules')} />
              <CardBody density="flush">
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('platform:overview.columns.module')}</Th>
                        <Th>{t('platform:overview.columns.availability')}</Th>
                        <Th align="right">{t('platform:overview.columns.tenants')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {query.data.modules.map((row) => (
                        <Tr key={row.code}>
                          <Td>{row.name}</Td>
                          <Td>
                            <Badge tone={row.status === 'AVAILABLE' ? 'green' : 'neutral'}>
                              {t(`platform:moduleStatus.${row.status}`)}
                            </Badge>
                          </Td>
                          <Td numeric align="right">
                            {row.tenants}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </CardBody>
            </Card>
          </div>

          <div className="grid-2">
            <Card>
              <CardHeader
                title={t('platform:overview.trials')}
                icon={<Clock className="ico ico-16" aria-hidden="true" />}
              />
              <CardBody density="flush">
                {query.data.trialsEndingSoon.length === 0 ? (
                  <EmptyState title={t('platform:overview.noTrial')} />
                ) : (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('platform:tenants.columns.tenant')}</Th>
                          <Th>{t('platform:overview.columns.trialEnds')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {query.data.trialsEndingSoon.map((row) => {
                          const ends = row.subscription?.trialEndsAt;
                          const days = ends ? daysUntil(ends) : null;
                          return (
                            <Tr key={row.id}>
                              <Td>
                                <Link
                                  to="/platform/tenants/$tenantId"
                                  params={{ tenantId: row.id }}
                                >
                                  {row.tradeName ?? row.legalName}
                                </Link>
                              </Td>
                              <Td>
                                {ends ? (
                                  <span className="row" style={{ gap: 6 }}>
                                    {formatDateShort(ends, i18n.language)}
                                    <Badge tone={days !== null && days < 0 ? 'red' : 'amber'}>
                                      {days !== null && days < 0
                                        ? t('platform:overview.trialExpired')
                                        : t('platform:overview.trialDays', { count: days ?? 0 })}
                                    </Badge>
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader title={t('platform:overview.recent')} />
              <CardBody density="flush">
                {query.data.recentTenants.length === 0 ? (
                  <EmptyState title={t('platform:tenants.empty')} />
                ) : (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('platform:tenants.columns.tenant')}</Th>
                          <Th>{t('platform:tenants.columns.status')}</Th>
                          <Th>{t('platform:tenants.columns.created')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {query.data.recentTenants.map((row) => (
                          <Tr key={row.id}>
                            <Td>
                              <Link to="/platform/tenants/$tenantId" params={{ tenantId: row.id }}>
                                {row.tradeName ?? row.legalName}
                              </Link>
                            </Td>
                            <Td>
                              <Badge tone={tenantStatusTone(row.status)}>
                                {t(`platform:status.${row.status}`)}
                              </Badge>
                            </Td>
                            <Td>{formatRelative(row.createdAt, i18n.language)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </Container>
  );
}
